/**
 * Blob-aware error normalization for the shared API layer (issue #2928).
 *
 * A request made with `responseType: "blob"` (file / CSV downloads) gets its
 * *error* body delivered as a `Blob` too, so {@link toApiError} sees neither a
 * JSON envelope nor a string: both arms of `getApiErrorMessage` are skipped and
 * the message degrades to the caller fallback, discarding whatever the server
 * actually said.
 *
 * This reads the blob body back to text before normalizing, so a download
 * failure carries the same message a JSON request would have carried.
 */

import axios from "axios";

import { ApiError, toApiError } from "./ApiError";

function isBlob(value: unknown): value is Blob {
  return typeof Blob !== "undefined" && value instanceof Blob;
}

/**
 * Decode a blob error body to the value {@link toApiError} expects: a parsed
 * object for a JSON envelope, the raw text otherwise. Returns `undefined` when
 * there is nothing usable, leaving the original body in place.
 */
async function decodeBlobBody(blob: Blob): Promise<unknown> {
  let text: string;
  try {
    text = await blob.text();
  } catch {
    // The decoded body is a nicety; a normalized error must still be produced.
    return undefined;
  }

  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    // Only an object envelope (`{ error }` / `{ message }`) beats the raw text —
    // a bare JSON scalar carries no more than the string it was parsed from.
    return parsed !== null && typeof parsed === "object" ? parsed : text;
  } catch {
    return text;
  }
}

/**
 * {@link toApiError} for a `responseType: "blob"` request. Async because
 * `Blob.text()` is; identical in every other respect, including idempotency on
 * an existing `ApiError`.
 */
export async function toApiErrorFromBlobResponse(error: unknown, fallbackMessage?: string): Promise<ApiError> {
  if (error instanceof ApiError) {
    return error;
  }

  if (axios.isAxiosError(error) && isBlob(error.response?.data)) {
    const decoded = await decodeBlobBody(error.response.data);
    if (decoded !== undefined) {
      // Substituted in place so `toApiError` stays the single copy of the
      // message-precedence rules (JSON `error`/`message` field, then a
      // plain-text body, then the caller fallback). The error was produced by
      // this layer's own request and is not observed anywhere else, and the
      // decoded body is strictly more useful than the Blob on `originalError`.
      (error.response as { data: unknown }).data = decoded;
    }
  }

  return toApiError(error, fallbackMessage);
}
